import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  addDays,
  differenceInDays,
  format,
  startOfWeek,
  startOfMonth,
} from "date-fns";
import toast, { Toaster } from "react-hot-toast";

const MAX_RANGE = 31;
const iso = (d) => format(d, "yyyy-MM-dd");
const toMidnight = (s) => new Date(`${s}T00:00`);

export default function DishesFilterBar({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  onFilter,
  dishesList = [],
  selectedDishes = [],
  onChangeDishes,
  categoriesList = [],
  selectedCategories = [],
  onChangeCategories,
  restaurantsList = [],
  selectedRestaurant = "all",
  onChangeRestaurant,
}) {
  /* ---------- stato date ---------- */
  const today = new Date();
  const [start, setStart] = useState(
    startDate ? toMidnight(startDate) : addDays(today, -6)
  );
  const [end, setEnd] = useState(endDate ? toMidnight(endDate) : today);

  /* ---------- validazione + autofilter ---------- */
  const validate = (s, e) => {
    if (e < s) {
      toast.error("La data di fine dev’essere successiva alla data di inizio");
      return false;
    }
    if (differenceInDays(e, s) > MAX_RANGE) {
      toast.error(`Intervallo massimo: ${MAX_RANGE} giorni`);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (validate(start, end)) onFilter?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    start,
    end,
    selectedDishes,
    selectedCategories,
    selectedRestaurant,
  ]);

  /* ---------- change handlers ---------- */
  const changeStart = (v) => {
    const s = toMidnight(v);
    setStart(s);
    onChangeStart?.(v);
    if (differenceInDays(end, s) > MAX_RANGE) {
      const fix = addDays(s, MAX_RANGE);
      const e = fix > today ? today : fix;
      setEnd(e);
      onChangeEnd?.(iso(e));
    }
  };
  const changeEnd = (v) => {
    const e = toMidnight(v);
    setEnd(e);
    onChangeEnd?.(v);
  };

  /* ---------- preset buttons ---------- */
  const presetToday = () => {
    setStart(today);
    setEnd(today);
    onChangeStart?.(iso(today));
    onChangeEnd?.(iso(today));
  };
  const presetWeek = () => {
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    setStart(monday);
    setEnd(today);
    onChangeStart?.(iso(monday));
    onChangeEnd?.(iso(today));
  };
  const presetMonth = () => {
    const first = startOfMonth(today);
    setStart(first);
    setEnd(today);
    onChangeStart?.(iso(first));
    onChangeEnd?.(iso(today));
  };

  /* ---------- select data ---------- */
  const dishOpts = dishesList.map((d) => ({ value: d.id, label: d.name }));
  const catOpts = categoriesList.map((c) => ({ value: c, label: c }));
  const restOpts = [
    { value: "all", label: "Tutti i ristoranti" },
    ...restaurantsList.map((r) => ({ value: String(r.id), label: r.name })),
  ];
  const selectStyles = {
    control: (b) => ({ ...b, minHeight: 38, borderRadius: 8 }),
    menu: (b) => ({ ...b, zIndex: 5 }),
  };

  /* ---------- UI ---------- */
  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: "#FFFAEE", color: "#924000", fontSize: ".9rem" },
          iconTheme: { primary: "#FF4500", secondary: "#FFF7E6" },
        }}
      />

      <div style={st.container}>
        {/* ===== 1. range date ===== */}
        <div style={st.row}>
          <input
            type="date"
            value={iso(start)}
            onChange={(e) => changeStart(e.target.value)}
            style={st.date}
          />
          <span style={{ margin: "0 .4rem", color: "#FFF" }}>→</span>
          <input
            type="date"
            value={iso(end)}
            onChange={(e) => changeEnd(e.target.value)}
            min={iso(start)}
            max={iso(addDays(start, MAX_RANGE))}
            style={st.date}
          />
        </div>

        {/* ===== 2. pulsanti rapidi ===== */}
        <div style={st.quickRow}>
          <button style={st.presetBtn} data-preset onClick={presetToday}>
            Oggi
          </button>
          <button style={st.presetBtn} data-preset onClick={presetWeek}>
            Settimana corrente
          </button>
          <button style={st.presetBtn} data-preset onClick={presetMonth}>
            Mese corrente
          </button>
        </div>

        {/* ===== 3. altri filtri ===== */}
        <div style={st.row}>
          <Select
            options={restOpts}
            value={restOpts.find((o) => o.value === String(selectedRestaurant))}
            onChange={(o) => onChangeRestaurant?.(o ? o.value : "all")}
            styles={selectStyles}
            placeholder="Ristorante"
          />

          <Select
            options={dishOpts}
            value={dishOpts.filter((o) => selectedDishes.includes(o.value))}
            onChange={(opts) =>
              onChangeDishes?.(opts ? opts.map((o) => o.value) : [])
            }
            isMulti
            styles={selectStyles}
            placeholder="Piatti"
          />

          <Select
            options={catOpts}
            value={catOpts.filter((o) => selectedCategories.includes(o.value))}
            onChange={(opts) =>
              onChangeCategories?.(opts ? opts.map((o) => o.value) : [])
            }
            isMulti
            styles={selectStyles}
            placeholder="Categorie"
          />

          <button
            style={st.mainBtn}
            data-main
            onClick={() => validate(start, end) && onFilter?.()}
          >
            Filtra
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------- styles ---------- */
const baseBtn = {
  border: "none",
  cursor: "pointer",
  transition: "background .15s,color .15s,box-shadow .15s",
  fontWeight: 600,
};

const st = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: ".8rem",
    padding: "1rem",
    background: "linear-gradient(135deg,#FF7A00 0%,#FF3C00 100%)",
    borderRadius: 12,
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: ".5rem",
    alignItems: "center",
  },
  quickRow: {
    display: "flex",
    gap: ".5rem",
    flexWrap: "wrap",
  },
  date: {
    padding: ".45rem .7rem",
    borderRadius: 8,
    border: "none",
    fontSize: ".9rem",
  },
  /* pulsante bianco principale */
  mainBtn: {
    ...baseBtn,
    padding: ".55rem 1.1rem",
    borderRadius: 24,
    background: "#FFFFFF",
    color: "#FF4500",
    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
  },
  /* pulsanti preset trasparenti */
  presetBtn: {
    ...baseBtn,
    padding: ".35rem .9rem",
    borderRadius: 24,
    background: "transparent",
    color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.6)",
  },
};

/* hover via CSS injection */
const injectHover = (sel, obj) => {
  const style = document.createElement("style");
  style.innerHTML = `${sel}:hover{${Object.entries(obj)
    .map(([k, v]) => `${k}:${v}`)
    .join(";")}}`;
  document.head.appendChild(style);
};
injectHover("button[data-main]", { background: "#FF4500", color: "#FFF" });
injectHover("button[data-preset]", {
  background: "rgba(255,255,255,0.15)",
});
